import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/components/auth/AuthLayout';
import { api } from '@/lib/customFetch';
import { useUser } from '@/contexts/UserContext';
import { mfaCodeSchema, type MfaCodeInput } from '@/lib/auth-schemas';

type SetupResponse = { secret: string; otpauth: string };
type LocationState = { pendingToken?: string };

export function MfaSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingToken = (location.state as LocationState | null)?.pendingToken;
  const { refresh } = useUser();
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MfaCodeInput>({ resolver: zodResolver(mfaCodeSchema) });

  useEffect(() => {
    if (!pendingToken) {
      navigate('/login', { replace: true });
      return;
    }
    void (async () => {
      try {
        const data = await api.post<SetupResponse>('/auth/mfa/setup-pending', undefined, {
          headers: { Authorization: `Bearer ${pendingToken}` },
        });
        setSetup(data);
      } catch {
        // toast já mostrado por customFetch
      }
    })();
  }, [pendingToken, navigate]);

  const onSubmit = handleSubmit(async ({ code }) => {
    if (!pendingToken) return;
    try {
      await api.post(
        '/auth/mfa/verify-pending',
        { code },
        { headers: { Authorization: `Bearer ${pendingToken}` } },
      );
      toast.success('MFA habilitado.');
      await refresh();
      navigate('/', { replace: true });
    } catch {
      // toast já mostrado por customFetch
    }
  });

  if (!pendingToken) return null;

  return (
    <div className="container mx-auto max-w-md px-4 py-8">
      <h1 className="text-2xl font-semibold">Configurar MFA</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Escaneie o QR no Google Authenticator, 1Password, Authy etc., depois informe o código de 6
        dígitos.
      </p>
      {!setup ? (
        <p className="mt-6 text-sm">Carregando...</p>
      ) : (
        <>
          <div className="bg-background mt-6 flex justify-center rounded-md border p-6">
            <QRCodeSVG value={setup.otpauth} size={200} />
          </div>
          <p className="text-muted-foreground mt-2 text-center text-xs">
            Ou use o código manual:{' '}
            <code className="bg-muted rounded px-1 font-mono">{setup.secret}</code>
          </p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="code">Código de 6 dígitos</Label>
              <Input
                id="code"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                {...register('code')}
              />
              {errors.code && <FieldError>{errors.code.message}</FieldError>}
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full">
              Verificar
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
